import { type ReactElement, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { NotFoundScreen } from '@/pages/NotFoundScreen';
import { useDelayedFlag } from '@/shared/hooks';
import { iconSizes, s } from '@/theme/tokens';
import { Panel } from '@/ui/composites/Panel';
import { usePageMetadata } from '@/ui/hooks/usePageTitle';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Box, CircularProgress, Text } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { PageShell } from '@/ui/primitives/PageShell';
import {
  RequireAdminAccess,
  RequireAuth,
  RequireFeatureFlag,
  RequireOnboarding,
  RequireSuperuserAccess,
} from './guards/RequireAuth';
import { RequireDesktop } from './guards/RequireDesktop';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import type { AppRouteDefinition } from './routes';
import type { AuthError, AuthSession } from '@/auth/types';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

type AppRoutesProps = {
  routeDefinitions: AppRouteDefinition[];
  featureFlagsLoading?: boolean;
  featureFlags?: Record<string, boolean> | null;
  session: AuthSession | null;
  authLoading: boolean;
  projectsLoaded: boolean;
  authError: AuthError | null;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signUp: (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    acceptedPolicies: boolean;
  }) => Promise<void>;
  signOut: () => void;
  refreshSession: () => Promise<boolean>;
  setSessionDirect: (next: AuthSession | null) => void;
  isAdmin: boolean;
  isSuperuser: boolean;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  projects: ProjectDto[];
  onAddProject: () => void;
};

export const AppRoutes = ({
  routeDefinitions,
  featureFlagsLoading,
  featureFlags,
  session,
  authLoading,
  projectsLoaded,
  authError,
  signIn,
  signUp,
  signOut,
  refreshSession,
  setSessionDirect,
  isAdmin,
  isSuperuser,
  organisations,
  projects,
  onAddProject,
}: AppRoutesProps): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const allowMobileRouteKeys = new Set([
    'landing',
    'sign-in',
    'sign-up',
    'auth-callback',
    'session-expired',
  ]);
  const wrapRoute = (
    element: ReactElement,
    routeKey?: string,
    title?: string,
    description?: string,
    suspenseFallbackLabel?: string,
    hideSuspenseSpinner = false,
  ): ReactElement => (
    <RouteErrorBoundary routeKey={routeKey}>
      <PageTitle title={title} description={description}>
        <Suspense
          fallback={
            <DelayedRouteLoadingFallback
              label={suspenseFallbackLabel}
              hideSpinner={hideSuspenseSpinner}
            />
          }
        >
          {element}
        </Suspense>
      </PageTitle>
    </RouteErrorBoundary>
  );

  const resolveSuspenseFallbackLabel = (route: AppRouteDefinition): string | undefined => {
    if (route.suspenseFallbackLabel) {
      return route.suspenseFallbackLabel;
    }
    if (!route.title) return undefined;
    return `Loading ${route.title.toLowerCase()}...`;
  };

  const routeContext = {
    session,
    authLoading,
    authError,
    signIn,
    signUp,
    signOut,
    refreshSession,
    setSessionDirect,
    isAdmin,
    organisations,
    projects,
    onAddProject,
  };
  const noProjectsGateRouteKeys = new Set([
    'execution-runs',
    'suites',
    'tests',
    'pipelines',
    'stages',
    'jobs',
    'features',
    'releases',
  ]);
  const selectedOrganisationExists =
    selectedOrganisationId !== null &&
    organisations.some((organisation) => organisation.id === selectedOrganisationId);
  const hasProjectsForSelectedOrganisation =
    selectedOrganisationId !== null &&
    projects.some((project) => project.organisationId === selectedOrganisationId);

  return (
    <Routes>
      {routeDefinitions
        .filter((route) => !(route.devOnly && !import.meta.env.DEV))
        .map((route) => {
          let element = route.element(routeContext);
          const shouldShowNoProjectsGate =
            route.requiresAuth === true &&
            noProjectsGateRouteKeys.has(route.key) &&
            authError === null &&
            projectsLoaded &&
            selectedOrganisationExists &&
            !hasProjectsForSelectedOrganisation;
          if (shouldShowNoProjectsGate) {
            element = <NoProjectsListGate onAddProject={onAddProject} />;
          }
          if (route.requiresAuth || route.requiresAdmin || route.requiresSuperuser) {
            element = (
              <RequireAuth
                session={session}
                loading={authLoading}
                authError={authError}
                refreshSession={refreshSession}
                featureFlagsLoading={featureFlagsLoading}
                featureFlags={featureFlags}
              >
                {element}
              </RequireAuth>
            );
          }
          if (route.requiresAuth || route.requiresAdmin || route.requiresSuperuser) {
            element = <RequireOnboarding session={session}>{element}</RequireOnboarding>;
          }
          if (route.featureFlag) {
            element = (
              <RequireFeatureFlag
                featureFlag={route.featureFlag}
                featureFlagsLoading={featureFlagsLoading}
                featureFlags={featureFlags}
              >
                {element}
              </RequireFeatureFlag>
            );
          }
          if (route.requiresAdmin) {
            element = <RequireAdminAccess canAccess={isAdmin}>{element}</RequireAdminAccess>;
          }
          if (route.requiresSuperuser) {
            element = (
              <RequireSuperuserAccess canAccess={isSuperuser}>{element}</RequireSuperuserAccess>
            );
          }
          element = (
            <RequireDesktop allowOnMobile={allowMobileRouteKeys.has(route.key)}>
              {element}
            </RequireDesktop>
          );
          return (
            <Route
              key={route.key}
              path={route.path}
              element={wrapRoute(
                element,
                route.key,
                route.title,
                route.description,
                resolveSuspenseFallbackLabel(route),
                route.hideSuspenseSpinner,
              )}
            />
          );
        })}
      <Route
        path="*"
        element={wrapRoute(
          <RequireDesktop>
            <NotFoundScreen />
          </RequireDesktop>,
          'not-found',
          'Page Not Found',
          undefined,
        )}
      />
    </Routes>
  );
};

const RouteLoadingFallback = ({
  label,
  hideSpinner = false,
}: {
  label?: string;
  hideSpinner?: boolean;
}): ReactElement => (
  <Box
    sx={{
      minHeight: '45vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Stack gap="md" align="center">
      <Text role="body" color="textSecondary" align="center">
        {label ?? 'Loading...'}
      </Text>
      {hideSpinner ? null : <CircularProgress size={iconSizes.lg} />}
    </Stack>
  </Box>
);

const DelayedRouteLoadingFallback = ({
  label,
  hideSpinner = false,
}: {
  label?: string;
  hideSpinner?: boolean;
}): ReactElement | null => {
  const showFallback = useDelayedFlag(true, 200);

  if (!showFallback) {
    return null;
  }

  return <RouteLoadingFallback label={label} hideSpinner={hideSpinner} />;
};

const NoProjectsListGate = ({ onAddProject }: { onAddProject: () => void }): ReactElement => (
  <PageShell maxWidth="xs" paddingX="lg" paddingY="xl" paddingBottom="xl">
    <Stack gap="xl" sx={{ pt: s('5xl') }}>
      <Section>
        <Box sx={{ width: '100%' }}>
          <Panel
            title="No Projects Found"
            titleSx={{ width: '100%', textAlign: 'center' }}
            actionsAlign="center"
            actions={<Button onClick={onAddProject}>Add Project</Button>}
          >
            <Text role="bodyMuted" align="center">
              Add a project to start adding test results.
            </Text>
          </Panel>
        </Box>
      </Section>
    </Stack>
  </PageShell>
);

type PageTitleProps = { title?: string; children: ReactElement };

type PageTitleWithDescriptionProps = PageTitleProps & { description?: string };

const PageTitle = ({
  title,
  description,
  children,
}: PageTitleWithDescriptionProps): ReactElement => {
  usePageMetadata(title, description);
  return children;
};
